import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users, CalendarCheck, ClipboardText, Buildings, ArrowRight, Clock, ShieldCheck, Money, ChatCircleDots, CurrencyCircleDollar,
} from "@phosphor-icons/react";

function StatCard({ icon: Icon, label, value, tone = "primary", delay = 0 }) {
  const isNeg = typeof value === "number" && value < 0;
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    muted: "bg-muted text-foreground",
    danger: "bg-destructive/10 text-destructive",
  };
  const iconTone = isNeg ? tones.danger : tones[tone];
  return (
    <Card className="p-6 border-border shadow-none transition-transform duration-200 hover:-translate-y-1 animate-fade-up"
      style={{ animationDelay: `${delay}ms` }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${iconTone}`}>
        <Icon weight="duotone" className="h-6 w-6" />
      </div>
      <p className={`mt-4 text-3xl font-heading font-extrabold tracking-tight ${isNeg ? "text-destructive" : ""}`}>{value ?? "—"}</p>
      <p className="text-sm text-muted-foreground mt-1">{label}{isNeg ? " (overdrawn)" : ""}</p>
    </Card>
  );
}

function ActionButton({ icon: Icon, label, testid, to, navigate }) {
  return (
    <Button variant="outline" data-testid={testid} onClick={() => navigate(to)}
      className="justify-between h-auto py-4 px-4 rounded-lg">
      <span className="flex items-center gap-3"><Icon weight="duotone" className="h-5 w-5 text-primary" /> {label}</span>
      <ArrowRight className="h-4 w-4" />
    </Button>
  );
}

export default function Dashboard() {
  const { user, canApprove, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">
          {isAdmin ? "Company Overview" : "Overview"}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
          {isAdmin ? "Company Dashboard" : `Hello, ${user?.name?.split(" ")[0]} 👋`}
        </h1>
        <p className="text-muted-foreground mt-2">
          {isAdmin
            ? "Everything about the company and HR operations at a glance."
            : "Here's what's happening across your workspace today."}
        </p>
      </div>

      {isAdmin ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard icon={Users} label="Total employees" value={stats?.total_employees} tone="primary" delay={0} />
            <StatCard icon={CalendarCheck} label="On leave today" value={stats?.on_leave_today} tone="warning" delay={60} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-6 lg:col-span-2 border-border shadow-none animate-fade-up" style={{ animationDelay: "220ms" }}>
              <h2 className="font-heading text-xl font-bold tracking-tight">Company actions</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <ActionButton icon={ClipboardText} label="Review Approvals" testid="quick-approvals" to="/approvals" navigate={navigate} />
                <ActionButton icon={Money} label="Payroll" testid="quick-payroll" to="/payroll" navigate={navigate} />
              </div>
            </Card>

            <Card className="p-6 border-border shadow-none bg-primary text-primary-foreground animate-fade-up"
              style={{ animationDelay: "260ms" }}>
              <ShieldCheck weight="duotone" className="h-8 w-8" />
              <h3 className="font-heading text-lg font-bold mt-3">Admin access</h3>
              <p className="text-sm text-primary-foreground/80 mt-2 leading-relaxed">
                This is the company workspace. You manage employee records, payroll and leave approvals here.
                Personal leave and payslips are handled from a separate staff account.
              </p>
            </Card>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={CalendarCheck} label="Vacation Leave credits" value={stats?.my_vl_credits} tone="success" delay={0} />
            <StatCard icon={CalendarCheck} label="Sick Leave credits" value={stats?.my_sl_credits} tone="success" delay={60} />
            <StatCard icon={CalendarCheck} label="Other credits" value={stats?.my_others_credits} tone="success" delay={120} />
            <StatCard icon={CurrencyCircleDollar} label="Loan balance"
              value={stats ? `₱${Number(stats.my_loan_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : undefined}
              tone="muted" delay={180} />
            <StatCard icon={Clock} label="My pending requests" value={stats?.my_pending} tone="warning" delay={240} />
            {stats?.pending_approvals !== undefined && (
              <StatCard icon={ClipboardText} label="Pending approvals" value={stats?.pending_approvals} tone="warning" delay={300} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-6 lg:col-span-2 border-border shadow-none animate-fade-up" style={{ animationDelay: "220ms" }}>
              <h2 className="font-heading text-xl font-bold tracking-tight">Quick actions</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <ActionButton icon={Users} label="Employee Directory" testid="quick-directory" to="/directory" navigate={navigate} />
                <ActionButton icon={CalendarCheck} label="Request Leave" testid="quick-leave" to="/leave" navigate={navigate} />
                {canApprove && (
                  <ActionButton icon={ClipboardText} label="Review Approvals" testid="quick-approvals" to="/approvals" navigate={navigate} />
                )}
                <ActionButton icon={Buildings} label="My Profile" testid="quick-profile" to="/profile" navigate={navigate} />
              </div>
            </Card>

            <Card className="p-6 border-border shadow-none bg-primary text-primary-foreground animate-fade-up"
              style={{ animationDelay: "260ms" }}>
              <ShieldCheck weight="duotone" className="h-8 w-8" />
              <h3 className="font-heading text-lg font-bold mt-3">Access level</h3>
              <p className="text-sm text-primary-foreground/80 mt-2 leading-relaxed">
                {canApprove
                  ? "You can view the directory and approve or reject leave requests from staff."
                  : "Sensitive files such as salary and documents are restricted to administrators."}
              </p>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
